import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Bookmark, Check, Search, SlidersHorizontal } from "@digital-twins/design-system/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { resolveAudienceCanonicalCoverUrl, resolveAudienceDigitalTwinCount, isSetupPickerAudience } from "@/pages/AudiencesPage/utils/audience-presentation";
import { isRelaunchingStudyAudienceStatus, isSelectableStudyAudienceStatus } from "@/lib/audience-selectability";
import { RelaunchingAudienceDialog } from "@/components/audiences/RelaunchingAudienceDialog";
import { AudienceSharingGlyph } from "@/components/audiences/AudienceSharingGlyph";
import {
  TooltipProvider,
} from "@/components/ui/tooltip";
import { setupStrings } from "../strings";
import { AudienceFilterModal } from "../../AudienceFilterModal";
import type { GoalCategoryId } from "../types";
import type { GoalDefinition } from "../goalsConfig";
import type { Population } from "@/types/routes/tests";
import type { PopulationFilter } from "@/types/routes/test-results";
import type { AudienceFilterLabel, SelectedSegment } from "@digital-twins/shared-types";

/**
 * StudySetupModal — the confirmation beat between picking a goal and the
 * questionnaire. Modeled on Conversations' ComposeModal: a two-panel dialog
 * where the left panel chooses the audience (searchable list + study type) and
 * the right panel previews it (cover, twin count, tagline) with the primary
 * "Continue" CTA.
 *
 * State model (see docs/plans/study-setup-audience-modal.md):
 * - Audience and filter apply to creation state IMMEDIATELY via the
 *   callbacks (consistent with the inline setup row + bookmarked auto-preselect).
 * - "Continue" only commits the goal (onContinue); the modal carries no draft
 *   state that needs flushing.
 * - Filtering is delegated wholesale to the reused AudienceFilterModal — no
 *   filter logic is reimplemented here. The applied filter is mirrored locally
 *   only to render the active-filter summary, and is cleared when the selected
 *   audience changes.
 */

interface StudySetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The pending goal — drives the study-type chip and the "Continue" CTA. Null
   * when the modal is opened from the inline setup row to change the audience
   * before a goal is committed; in that case the CTA just saves and closes.
   */
  goal: GoalDefinition | null;
  populations: Array<Population>;
  /** Controlled current selection (the bookmarked auto-preselect by default). */
  selectedPopulationId: string;
  onSelectPopulation: (
    populationId: string,
    filter: PopulationFilter | null,
    filteredCount: number | null,
    selectedSegments?: Array<SelectedSegment>,
    filterLabels?: Array<AudienceFilterLabel>,
  ) => void;
  /** Commit the goal and advance to the questionnaire. */
  onContinue: () => void;
  /** When true, the filter sub-modal opens automatically once the dialog mounts. */
  openFilterImmediately?: boolean;
  /**
   * Optional CTA label override. Quick Study passes "Start study" (the Continue
   * fires the AI kickoff directly, there is no questionnaire step). Regular
   * Studies omit it → the goal-aware default label is used.
   */
  continueLabel?: string;
  /**
   * Optional CTA `data-testid` override. Quick Study passes `qs-start-room`;
   * Regular Studies omit it → the shared `study-setup-continue` testid.
   */
  continueTestId?: string;
}

/** Category accents — identical to GoalCard's TAG_ACCENTS. */
const TAG_ACCENTS: Record<GoalCategoryId, string> = {
  test: "bg-emerald-500/10 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-300/25",
  brand: "bg-sky-500/10 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-300/25",
  explore: "bg-amber-500/10 text-amber-700 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-300/25",
};

/** Bookmarked audiences float to the top, then alphabetical — mirrors the picker. */
function sortForPicker(populations: Array<Population>): Array<Population> {
  return [...populations].sort((a, b) => {
    if (Boolean(a.isBookmarked) !== Boolean(b.isBookmarked)) {
      return a.isBookmarked ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
}

export function StudySetupModal({
  open,
  onOpenChange,
  goal,
  populations,
  selectedPopulationId,
  onSelectPopulation,
  onContinue,
  openFilterImmediately,
  continueLabel,
  continueTestId,
}: StudySetupModalProps) {
  const [query, setQuery] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    if (open && openFilterImmediately) {
      setIsFilterModalOpen(true)
    }
  }, [open, openFilterImmediately]);
  const [brokenCoverIds, setBrokenCoverIds] = useState<Set<string>>(() => new Set());
  const [pendingRelaunchId, setPendingRelaunchId] = useState<string | null>(null);
  // Local mirror of the applied filter, for the preview summary only. The
  // parent remains the source of truth via onSelectPopulation.
  const [activeFilteredCount, setActiveFilteredCount] = useState<number | null>(
    null,
  );

  const selectedPopulation = populations.find(
    (population) => population.id === selectedPopulationId,
  );

  const pickerPopulations = useMemo(
    () => populations.filter((population) =>
      isSetupPickerAudience(population) && !brokenCoverIds.has(population.id),
    ),
    [populations, brokenCoverIds],
  );

  const visiblePopulations = useMemo(() => {
    const sorted = sortForPicker(pickerPopulations);
    const normalized = query.trim().toLowerCase();
    if (!normalized) return sorted;
    return sorted.filter((population) =>
      population.name.toLowerCase().includes(normalized),
    );
  }, [pickerPopulations, query]);

  // Reset transient state whenever the modal opens fresh. The filtered-count
  // mirror must reset too, otherwise a stale "Filtered to N twins" preview can
  // survive a close→reopen for a different audience.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveFilteredCount(null);
      setBrokenCoverIds(new Set());
    }
  }, [open]);

  function handleCoverError(populationId: string) {
    setBrokenCoverIds((prev) => {
      if (prev.has(populationId)) return prev;
      const next = new Set(prev);
      next.add(populationId);
      return next;
    });
  }

  const handleSelectAudience = (populationId: string) => {
    if (populationId === selectedPopulationId) return;
    const population = populations.find((p) => p.id === populationId);
    if (!population || !isSelectableStudyAudienceStatus(population.status)) return;
    if (isRelaunchingStudyAudienceStatus(population.status)) {
      setPendingRelaunchId(populationId);
      return;
    }
    // Switching audiences clears any filter from the previous population.
    setActiveFilteredCount(null);
    onSelectPopulation(populationId, null, null);
  };

  function confirmRelaunchSelection() {
    if (!pendingRelaunchId) {
      return;
    }
    const populationId = pendingRelaunchId;
    setPendingRelaunchId(null);
    setActiveFilteredCount(null);
    onSelectPopulation(populationId, null, null);
  }

  const handleFilterApply = (
    filter: PopulationFilter,
    filteredCount: number,
    // The tag names are the segments' labels: the segments carry them.
    _appliedTagNames?: Array<string>,
    selectedSegments?: Array<SelectedSegment>,
    filterLabels?: Array<AudienceFilterLabel>,
  ) => {
    if (!selectedPopulationId) return;
    setIsFilterModalOpen(false);
    setActiveFilteredCount(filteredCount);
    onSelectPopulation(
      selectedPopulationId,
      filter,
      filteredCount,
      selectedSegments,
      filterLabels,
    );
  };

  const handleContinueWithout = () => {
    if (!selectedPopulationId) return;
    setIsFilterModalOpen(false);
    setActiveFilteredCount(null);
    onSelectPopulation(selectedPopulationId, null, null);
  };

  const coverUrl = resolveAudienceCanonicalCoverUrl(selectedPopulation ?? null);
  const tagline =
    selectedPopulation?.stageMetadata?.tagline ||
    selectedPopulation?.aiDescription ||
    null;
  const baseCount = selectedPopulation?.personaCount ?? null;
  const displayCount =
    activeFilteredCount !== null ? activeFilteredCount : baseCount;

  return (
    <TooltipProvider delayDuration={300}>
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex h-[min(560px,85vh)] w-[calc(100vw-2rem)] max-w-[900px] flex-col overflow-hidden rounded-2xl p-0 sm:p-0 sm:flex-row"
          data-testid="study-setup-modal"
        >
          <DialogTitle className="sr-only">{setupStrings.modalTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {setupStrings.modalSubtitle}
          </DialogDescription>

          {/* ── Left: audience picker ── */}
          <div
            data-testid="study-setup-picker-pane"
            className="flex min-h-0 w-full flex-1 flex-col overflow-hidden border-b border-border p-6 sm:w-[400px] sm:flex-none sm:shrink-0 sm:border-b-0 sm:border-r"
          >
            {goal && (
              <span
                data-testid="study-setup-method-tag"
                className="inline-flex w-fit items-center rounded-full px-3 py-[3px] text-[12px] font-medium leading-[1.4] bg-white text-foreground ring-1 ring-border dark:bg-card"
              >
                {goal.methodTag}
              </span>
            )}
            <h2 className="mt-3 font-display text-2xl leading-tight text-foreground dark:text-white">
              {setupStrings.modalTitle}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {setupStrings.modalSubtitle}
            </p>

            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={setupStrings.modalSearchPlaceholder}
                className="pl-9"
                data-testid="study-setup-audience-search"
              />
            </div>

            <div className="mt-2 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
              {visiblePopulations.length === 0 && (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                  {query.trim()
                    ? setupStrings.modalEmpty
                    : setupStrings.modalNoAudiences}
                </p>
              )}
              {visiblePopulations.map((population) => {
                const isSelected = population.id === selectedPopulationId;
                const itemCoverUrl = resolveAudienceCanonicalCoverUrl(population);
                const twinCount = resolveAudienceDigitalTwinCount(population);
                return (
                  <button
                    key={population.id}
                    type="button"
                    data-testid={`study-setup-audience-option-${population.id}`}
                    aria-pressed={isSelected}
                    onClick={() => handleSelectAudience(population.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                      isSelected
                        ? "border-primary/50 bg-primary/5"
                        : "border-transparent hover:border-border hover:bg-muted/50",
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-primary/10">
                      <img
                        src={itemCoverUrl!}
                        alt=""
                        aria-hidden
                        className="h-full w-full object-cover"
                        onError={() => handleCoverError(population.id)}
                      />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex items-center gap-1.5">
                        <AudienceSharingGlyph
                          audience={population}
                          dataTestId={`study-setup-sharing-glyph-${population.id}`}
                        />
                        <span className="truncate text-sm text-foreground">
                          {population.name}
                        </span>
                        {population.isBookmarked && (
                          <Bookmark
                            aria-label={setupStrings.bookmarkedBadge}
                            className="h-3.5 w-3.5 shrink-0 fill-primary/30 text-primary"
                          />
                        )}
                      </span>
                      {typeof twinCount === "number" && (
                        <span className="text-xs text-muted-foreground">
                          {setupStrings.twinsCount(twinCount)}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: preview ── */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="relative flex-1 overflow-hidden">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={selectedPopulation?.name ?? ""}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-primary/10 to-muted/30" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 px-6 pb-5">
              {typeof displayCount === "number" && (
                <p className="mb-2 text-xs font-medium text-foreground/70 drop-shadow-sm">
                  {activeFilteredCount !== null
                    ? setupStrings.filteredTwinsCount(displayCount)
                    : setupStrings.twinsCount(displayCount)}
                </p>
              )}
                <h3 className="font-display text-2xl text-foreground drop-shadow-sm">
                  {selectedPopulation?.name ?? setupStrings.audienceEmpty}
                </h3>
                {tagline && (
                  <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground drop-shadow-sm">
                    {tagline}
                  </p>
                )}
              </div>
            </div>

            <div className="shrink-0 space-y-3 border-t border-border px-6 py-5">
              <Button
                variant="outline"
                onClick={() => setIsFilterModalOpen(true)}
                disabled={!selectedPopulationId}
                className="w-full"
                data-testid="study-setup-refine-audience"
              >
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                {setupStrings.modalRefineAudience}
              </Button>
              <Button
                variant="cta"
                onClick={onContinue}
                disabled={!selectedPopulationId}
                className="h-11 w-full rounded-xl"
                data-testid={continueTestId ?? "study-setup-continue"}
              >
                {continueLabel ?? (goal ? setupStrings.modalContinue : setupStrings.modalSaveAudience)}
                {goal && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RelaunchingAudienceDialog
        open={pendingRelaunchId !== null}
        onCancel={() => setPendingRelaunchId(null)}
        onConfirm={confirmRelaunchSelection}
      />

      {/* Filtering is delegated to the shared, sensitive AudienceFilterModal. */}
      {selectedPopulation && (
        <AudienceFilterModal
          key={selectedPopulation.id}
          open={isFilterModalOpen}
          onOpenChange={setIsFilterModalOpen}
          populationId={selectedPopulation.id}
          populationName={selectedPopulation.name}
          coverImageUrl={resolveAudienceCanonicalCoverUrl(selectedPopulation)}
          prefetchedData={null}
          audienceType={selectedPopulation.audienceType ?? null}
          onFilterApply={handleFilterApply}
          onContinueWithout={handleContinueWithout}
          audienceTags={selectedPopulation.audienceTags}
          forStudy
        />
      )}
    </>
    </TooltipProvider>
  );
}
